import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { documentationLinks } from "../config/documentationLinks";
import { services } from "../config/services";
import { useI18n } from "../i18n/I18nContext";
import { useDocsNavigation } from "./DocsNavigationContext";
import MiddlewareQrModal from "./MiddlewareQrModal";
import ServiceIcon from "./ServiceIcon";

const navigationServices = services.filter((service) => !service.hideFromDashboard);

const DocsLayout = ({ actions, children, eyebrow, subtitle, title }) => {
    const location = useLocation();
    const { t } = useI18n();
    const { closeNav, isCompact, navOpen, sessionControls } = useDocsNavigation();
    const [servicesOpen, setServicesOpen] = useState(location.pathname !== "/");
    const [documentationOpen, setDocumentationOpen] = useState(true);
    const [selectedMiddlewareService, setSelectedMiddlewareService] = useState(null);

    const closeCompactNav = () => {
        if (isCompact) closeNav();
    };

    const openMiddlewareModal = (service) => {
        setSelectedMiddlewareService(service);
        closeCompactNav();
    };

    return (
        <main className={`docs-app-shell${navOpen ? "" : " is-nav-collapsed"}`}>
            {selectedMiddlewareService ? (
                <MiddlewareQrModal
                    service={selectedMiddlewareService}
                    onClose={() => setSelectedMiddlewareService(null)}
                />
            ) : null}

            <aside className={`docs-sidebar${navOpen ? " is-open" : ""}`} id="docs-sidebar">
                <nav className="docs-nav" aria-label={t("layout.services")}>
                    <Link
                        className={`docs-nav-feature${location.pathname === "/" ? " is-active" : ""}`}
                        to="/"
                        onClick={closeCompactNav}
                    >
                        <span className="docs-nav-icon">
                            <ServiceIcon name="doc" />
                        </span>
                        <span>{t("layout.contractsOpenApi")}</span>
                    </Link>

                    <button
                        aria-expanded={servicesOpen}
                        className="docs-nav-section"
                        type="button"
                        onClick={() => setServicesOpen((open) => !open)}
                    >
                        <span>{t("layout.services")}</span>
                        <span aria-hidden="true">{servicesOpen ? "^" : "v"}</span>
                    </button>

                    {servicesOpen ? (
                        <div className="docs-nav-group">
                            {navigationServices.map((service) => {
                                const active = service.route && location.pathname === service.route;
                                const content = (
                                    <>
                                        <span className="docs-nav-icon">
                                            <ServiceIcon name={service.icon} />
                                        </span>
                                        <span>{t(`services.${service.key}.shortName`)}</span>
                                    </>
                                );

                                if (service.externalUrl) {
                                    return (
                                        <button
                                            className="docs-nav-link"
                                            key={service.key}
                                            type="button"
                                            onClick={() => openMiddlewareModal(service)}
                                        >
                                            {content}
                                        </button>
                                    );
                                }

                                return (
                                    <Link
                                        className={`docs-nav-link${active ? " is-active" : ""}`}
                                        key={service.key}
                                        to={service.route}
                                        onClick={closeCompactNav}
                                    >
                                        {content}
                                    </Link>
                                );
                            })}
                        </div>
                    ) : null}

                    <button
                        aria-expanded={documentationOpen}
                        className="docs-nav-section"
                        type="button"
                        onClick={() => setDocumentationOpen((open) => !open)}
                    >
                        <span>{t("layout.documentation")}</span>
                        <span aria-hidden="true">{documentationOpen ? "^" : "v"}</span>
                    </button>

                    {documentationOpen ? (
                        <div className="docs-nav-group">
                            {documentationLinks.map((item) => (
                                <a
                                    className="docs-nav-link"
                                    href={item.externalUrl}
                                    key={item.key}
                                    onClick={closeCompactNav}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <span className="docs-nav-icon">
                                        <ServiceIcon name={item.icon} />
                                    </span>
                                    <span>{t(`documentation.${item.key}.shortName`)}</span>
                                </a>
                            ))}
                        </div>
                    ) : null}
                </nav>

                {sessionControls ? (
                    <div className="docs-mobile-session">
                        <div className="docs-mobile-session-head">
                            <span>{sessionControls.username}</span>
                            <strong>{t("auth.tokenActive")}</strong>
                        </div>
                        <div className="docs-mobile-session-actions">
                            <button
                                type="button"
                                onClick={sessionControls.copySessionToken}
                                disabled={!sessionControls.sessionToken}
                            >
                                {sessionControls.tokenCopied ? t("auth.copied") : t("auth.copy")}
                            </button>
                        </div>
                    </div>
                ) : null}

                <div className="docs-sidebar-footer">
                    <span>{t("auth.cognito")}</span>
                    <strong>{t("auth.activeAuth")}</strong>
                </div>
            </aside>

            <section className="docs-main">
                <header className="docs-page-header">
                    <div>
                        <p className="docs-eyebrow">{eyebrow || t("auth.sandboxPrivate")}</p>
                        <h1>{title}</h1>
                        {subtitle ? <p>{subtitle}</p> : null}
                    </div>
                    {actions ? <div className="docs-page-actions">{actions}</div> : null}
                </header>
                {children}
            </section>
        </main>
    );
};

export default DocsLayout;
